import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSampleData } from "@/hooks/useSampleData";
import { needsMigration } from "@/db/migrations/v01ToV02";
import { FLAG_ONBOARDED, hasFlag, setFlag } from "@/lib/storage/flags";

/**
 * First-visit intro dialog for brand-new users. v0.1 upgraders see the
 * {@link MigrationDialog} instead (this stays hidden while a migration is due).
 */
export function OnboardingDialog(): JSX.Element {
  const [open, setOpen] = useState(() => !hasFlag(FLAG_ONBOARDED) && !needsMigration());
  const { loadSample, loading } = useSampleData();

  function dismiss(): void {
    setFlag(FLAG_ONBOARDED);
    setOpen(false);
  }

  async function trySample(): Promise<void> {
    setFlag(FLAG_ONBOARDED);
    setOpen(false);
    await loadSample();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Welcome to CatalogKit</DialogTitle>
          <DialogDescription>
            Clean and categorize PSA product-catalog exports for quoting — entirely in your browser.
            Your data never leaves your machine.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm text-muted-foreground">
          <li>Drop a ConnectWise (or custom) CSV to clean, categorize, and flag pricing issues.</li>
          <li>Search, filter, and download cleaned CSVs, per-category ZIPs, and a quality report.</li>
          <li>100% local — verify any time in DevTools → Network.</li>
        </ul>
        <DialogFooter>
          <Button variant="ghost" onClick={dismiss}>
            Skip
          </Button>
          <Button onClick={() => void trySample()} disabled={loading}>
            {loading ? "Loading…" : "Try with sample data"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
